import type { DynamicFieldSaveRequest } from "@/services/api/api-types";
import type {
  TriyoDynamicField,
  TriyoDynamicFieldTable,
  TriyoDynamicFieldTableCell,
  TriyoDynamicFieldTableColumn,
  TriyoDynamicFieldTableRow,
} from "@/types/models";
import type { DealTrackerColumnKind, DealTrackerSource, MergersAndAcquisitionsRow } from "./deal-tracker-types";

/** Label of the Table dynamic field holding the M&A deal rows (AppConstants.dealsTracker). */
export const DEAL_TRACKER_FIELD_LABEL = "Deal Tracker";

/** FieldType of a dynamic field that renders as a grid. */
const TABLE_FIELD_TYPE = "Table";

/**
 * The one column lock Triyo.Web honours unconditionally. Locks 2 and 3 depend on
 * the task's workflow permission, and for their own tracker a Junior Banker is
 * the task owner — which Triyo.Web's tableLock() treats as unlocked.
 */
const CELL_LOCKED_ALWAYS = 4;

/** Placeholder Triyo.Web renders for a cell with no value. */
export const EMPTY_CELL = "–";

function normalized(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

/**
 * Cell editor for a column, from the Field Repository's column type. Checkbox
 * and FileUpload columns have no add-in editor and render read-only rather than
 * risk writing a text value over structured data.
 */
export function columnKind(column: TriyoDynamicFieldTableColumn): DealTrackerColumnKind {
  switch (normalized(column.Type)) {
    case "string":
      return "text";
    case "lookup":
      return "list";
    case "date":
      return "date";
    case "numeric":
      return "number";
    default:
      return "unsupported";
  }
}

export function isCellLocked(cell: TriyoDynamicFieldTableCell): boolean {
  return cell.ColumnLockStatus === CELL_LOCKED_ALWAYS;
}

/**
 * Columns in display order, minus any the admin hid. Ordered by the configured
 * `Order` rather than trusting transport order — the same rule Create Deal's
 * renderer applies, and what decides which two columns end up frozen.
 */
export function visibleColumns(table: TriyoDynamicFieldTable): TriyoDynamicFieldTableColumn[] {
  return table.Columns.filter((column) => column.IsVisible !== false).sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
}

/** Selectable values for a List cell; the API pre-splits them per cell. */
export function cellOptions(cell: TriyoDynamicFieldTableCell, column: TriyoDynamicFieldTableColumn): string[] {
  if (cell.LookupValues?.length) return cell.LookupValues.filter(Boolean);
  return (column.LookupValues ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

export function findCell(row: TriyoDynamicFieldTableRow, columnId: number): TriyoDynamicFieldTableCell | undefined {
  return row.Data.find((cell) => cell.ColumnId === columnId);
}

export function isRowEmpty(row: TriyoDynamicFieldTableRow): boolean {
  return row.Data.every((cell) => !cell.Value?.trim());
}

/** Deal rows that actually carry data — the "{n} deals updated" count. */
export function populatedRowCount(table: TriyoDynamicFieldTable): number {
  return table.Rows.filter((row) => !isRowEmpty(row)).length;
}

/**
 * `Row` is unique across a table on read and stays unique for rows added here,
 * so it doubles as the client-side key that `Id` cannot be (every new row has
 * Id 0 until the API assigns one).
 */
function nextRowKey(rows: TriyoDynamicFieldTableRow[]): number {
  return rows.reduce((highest, row) => Math.max(highest, row.Row ?? 0), 0) + 1;
}

function blankCell(column: TriyoDynamicFieldTableColumn): TriyoDynamicFieldTableCell {
  return {
    Id: 0,
    ColumnId: column.Id,
    ColumnType: column.Type ?? null,
    ColumnName: column.Name ?? null,
    Value: "",
    LookupValues: (column.LookupValues ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean),
    ColumnLockStatus: column.LockStatus ?? 0,
  };
}

/** Appends an empty row ready for inline entry. Id 0 marks it as an insert. */
export function addRow(table: TriyoDynamicFieldTable): TriyoDynamicFieldTable {
  const row: TriyoDynamicFieldTableRow = {
    Id: 0,
    Row: nextRowKey(table.Rows),
    IsVisible: true,
    Data: visibleColumns(table).map(blankCell),
  };
  return { ...table, Rows: [...table.Rows, row] };
}

/**
 * Drops a row. A saved row simply disappears from the payload, which is how the
 * API is told to delete it.
 */
export function deleteRow(table: TriyoDynamicFieldTable, rowKey: number): TriyoDynamicFieldTable {
  return { ...table, Rows: table.Rows.filter((row) => row.Row !== rowKey) };
}

export function setCellValue(
  table: TriyoDynamicFieldTable,
  rowKey: number,
  columnId: number,
  value: string
): TriyoDynamicFieldTable {
  return {
    ...table,
    Rows: table.Rows.map((row) =>
      row.Row === rowKey
        ? { ...row, Data: row.Data.map((cell) => (cell.ColumnId === columnId ? { ...cell, Value: value } : cell)) }
        : row
    ),
  };
}

/** Compares only what a save would send, so cosmetic differences do not count. */
function comparable(table: TriyoDynamicFieldTable): string {
  return JSON.stringify(
    table.Rows.map((row) => ({
      Id: row.Id,
      Data: row.Data.map((cell) => [cell.ColumnId, cell.Value ?? ""]),
    }))
  );
}

export function hasChanges(original: TriyoDynamicFieldTable, current: TriyoDynamicFieldTable): boolean {
  return comparable(original) !== comparable(current);
}

/** A single row's comparable form -- its cells as [columnId, value] pairs. */
function rowCells(row: TriyoDynamicFieldTableRow): string {
  return JSON.stringify(row.Data.map((cell) => [cell.ColumnId, cell.Value ?? ""]));
}

/**
 * How many deal rows the save actually changes: an existing row whose cells
 * differ (update), a saved row dropped from the grid (delete), or a new row that
 * carries data (insert). This is the "{n} deals updated" count, so editing 2 of
 * 9 rows reads "2" rather than the whole populated set.
 */
export function changedRowCount(original: TriyoDynamicFieldTable, current: TriyoDynamicFieldTable): number {
  const currentById = new Map<number, string>();
  for (const row of current.Rows) {
    if (row.Id !== 0) currentById.set(row.Id, rowCells(row));
  }

  let count = 0;
  for (const row of original.Rows) {
    if (row.Id === 0) continue;
    const now = currentById.get(row.Id);
    if (now === undefined || now !== rowCells(row)) count += 1; // deleted or updated
  }
  for (const row of current.Rows) {
    if (row.Id === 0 && !isRowEmpty(row)) count += 1; // inserted
  }
  return count;
}

/**
 * The "[POST] /dynamic-fields" body for a Deal Tracker save. The whole grid goes
 * in one entry: the API inserts rows with Id 0, updates the rest by id, and
 * deletes every saved row missing from this payload.
 */
export function toSaveRequest(source: DealTrackerSource, table: TriyoDynamicFieldTable): DynamicFieldSaveRequest[] {
  return [
    {
      DynamicFieldId: source.dynamicFieldId,
      ObjectId: source.taskId,
      Table: {
        Columns: table.Columns,
        Rows: table.Rows.map((row) => ({
          Id: row.Id,
          Row: row.Row,
          IsVisible: row.IsVisible ?? true,
          // ColumnType has to survive the round trip: the API reformats "Date"
          // cells on the way in and stores the raw string without it.
          Data: row.Data.map((cell) => ({
            Id: cell.Id ?? 0,
            ColumnId: cell.ColumnId,
            ColumnType: cell.ColumnType ?? null,
            ColumnName: cell.ColumnName ?? null,
            Value: cell.Value ?? "",
            LookupValues: cell.LookupValues ?? null,
            ColumnLockStatus: cell.ColumnLockStatus ?? 0,
          })),
        })),
      },
    },
  ];
}

/** The Deal Tracker Table field among a task's dynamic fields. */
export function findDealTrackerField(fields: TriyoDynamicField[], dynamicFieldId?: number): TriyoDynamicField | null {
  const tables = fields.filter((field) => field.FieldType === TABLE_FIELD_TYPE && field.Table);
  if (dynamicFieldId) {
    const byId = tables.find((field) => field.Id === dynamicFieldId);
    if (byId) return byId;
  }
  return tables.find((field) => normalized(field.Label) === normalized(DEAL_TRACKER_FIELD_LABEL)) ?? null;
}

/**
 * Folds a widget row and its task's dynamic fields into the one object the
 * editor needs. Returns null when the row carries no tracker to edit.
 */
export function resolveDealTracker(
  type: string,
  row: MergersAndAcquisitionsRow,
  fields: TriyoDynamicField[]
): DealTrackerSource | null {
  const field = findDealTrackerField(fields, row.DealTrackerFieldId);
  if (!field?.Table || !row.DealsTaskId) return null;

  return {
    type,
    taskId: row.DealsTaskId,
    taskName: row.DealsTaskName ?? DEAL_TRACKER_FIELD_LABEL,
    dynamicFieldId: field.Id,
    table: {
      Columns: field.Table.Columns ?? [],
      Rows: field.Table.Rows ?? [],
    },
    canEdit: row.AllowDealsEdit === true,
    lastUpdate: row.LastUpdate && row.LastUpdate !== "N/A" ? row.LastUpdate : null,
  };
}
